import React from 'react';
import { motion } from 'framer-motion';
import { useModal } from '../services/ModalService';
import '../styles/pages/PayrollManagement.css';

const PayrollManagement = () => {
  const { openContactModal } = useModal();

  return (
    <div className="payroll-management">
      {/* Hero Section */}
      <section className="payroll-hero">
        <div className="payroll-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="payroll-hero-content"
          >
            <div className="hero-badge">
              💼 Payroll Excellence
            </div>
            
            <h1 className="hero-title"
            dangerouslySetInnerHTML={{ __html: "Comprehensive <span class=\"gradient-text\">Payroll Management</span><br />Services" }}
            />
            
            <p className="hero-subtitle">
              Streamline your payroll operations with our comprehensive management services, ensuring accuracy, compliance, and efficiency for your business.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn-primary"
                onClick={() => openContactModal(
                  'Payroll Management Services',
                  'Payroll Management Page - Get Started Today'
                )}
              >
                Get Started Today
              </button>
              <button 
                className="btn-secondary"
                onClick={() => openContactModal(
                  'Payroll Services Information',
                  'Payroll Management Page - Learn More'
                )}
              >
                Learn More
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="payroll-section">
        <div className="payroll-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="payroll-section-title gradient-text">Payroll Management Services</h2>
            <p className="payroll-section-subtitle">
              Complete payroll solutions to manage your workforce efficiently and compliantly
            </p>
            
            <div className="payroll-services-grid">
              
              <div className="payroll-glass-card">
                <h3>Payroll Processing</h3>
                <p>Accurate and timely payroll processing with comprehensive pay calculation and reporting.</p>
                <ul>
                  <li>✓ Bi-weekly & Monthly Processing</li>
                  <li>✓ Direct Deposit Setup</li>
                  <li>✓ Pay Stub Generation</li>
                  <li>✓ Overtime Calculations</li>
                </ul>
              </div>
              
              <div className="payroll-glass-card">
                <h3>Tax Management</h3>
                <p>Complete payroll tax management including calculations, filings, and compliance monitoring.</p>
                <ul>
                  <li>✓ Tax Withholding Calculations</li>
                  <li>✓ Quarterly Tax Filings</li>
                  <li>✓ Year-end W-2 Processing</li>
                  <li>✓ Multi-state Tax Compliance</li>
                </ul>
              </div>
              
              <div className="payroll-glass-card">
                <h3>Employee Benefits</h3>
                <p>Comprehensive benefits administration integrated with your payroll processing.</p>
                <ul>
                  <li>✓ Health Insurance Deductions</li>
                  <li>✓ 401(k) Contributions</li>
                  <li>✓ HSA/FSA Management</li>
                  <li>✓ Time-off Tracking</li>
                </ul>
              </div>
              
              <div className="payroll-glass-card">
                <h3>Compliance & Reporting</h3>
                <p>Ensure regulatory compliance with comprehensive reporting and documentation.</p>
                <ul>
                  <li>✓ Labor Law Compliance</li>
                  <li>✓ Audit Support</li>
                  <li>✓ Custom Reporting</li>
                  <li>✓ Record Keeping</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="payroll-section">
        <div className="payroll-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="payroll-section-title gradient-text">Why Choose Our Payroll Services</h2>
            <p className="payroll-section-subtitle">
              Professional expertise and reliable systems to manage your payroll efficiently
            </p>
            
            <div className="payroll-benefits-grid">
              
              <div className="payroll-benefit-card">
                <div className="payroll-benefit-icon">🎯</div>
                <h4 className="payroll-benefit-title">Accurate Processing</h4>
                <p className="payroll-benefit-description">Precise payroll calculations with error-free processing</p>
              </div>
              
              <div className="payroll-benefit-card">
                <div className="payroll-benefit-icon">⚖️</div>
                <h4 className="payroll-benefit-title">Full Compliance</h4>
                <p className="payroll-benefit-description">Stay compliant with all federal and state regulations</p>
              </div>
              
              <div className="payroll-benefit-card">
                <div className="payroll-benefit-icon">⏰</div>
                <h4 className="payroll-benefit-title">Timely Delivery</h4>
                <p className="payroll-benefit-description">On-time payroll processing and tax filings</p>
              </div>
              
              <div className="payroll-benefit-card">
                <div className="payroll-benefit-icon">🔒</div>
                <h4 className="payroll-benefit-title">Data Security</h4>
                <p className="payroll-benefit-description">Secure handling of sensitive employee information</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="payroll-section">
        <div className="payroll-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="payroll-cta-card">
              <h3 className="payroll-cta-title">
                Ready to Streamline Your Payroll?
              </h3>
              <p className="payroll-cta-subtitle">
                Contact us today to learn how our payroll management services can simplify your operations and ensure compliance.
              </p>
              <div className="payroll-cta-buttons">
                <button 
                  className="btn-primary"
                  onClick={() => openContactModal(
                    'Payroll Management Setup',
                    'Payroll Management Page - Start Payroll Management'
                  )}
                >
                  Start Payroll Management
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => openContactModal(
                    'Payroll Demo Request',
                    'Payroll Management Page - Schedule Demo'
                  )}
                >
                  Schedule Demo
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PayrollManagement;